import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, CheckCircle2, ArrowRight, AlertTriangle, ShieldCheck } from 'lucide-react';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
interface ComparisonPoint {
  title: string;
  description?: string;
}

interface CardProps {
  type: 'problem' | 'solution';
  title: string;
  points: ComparisonPoint[];
  footerTitle?: string;
  footerValue?: string;
  footerSub?: string;
}

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────
const PROBLEM_POINTS: ComparisonPoint[] = [
{ title: 'Surprise audit notice' },
{
  title: 'Two weeks of overtime',
  description: 'Digging through Google Drive, email chains, paper binders, Slack threads'
},
{
  title: 'Policies scattered across 5 systems',
  description: 'No version control. No one knows which version is approved.'
},
{
  title: 'Failed inspection',
  description: "Not because you weren't compliant — because you couldn't prove it."
}];


const SOLUTION_POINTS: ComparisonPoint[] = [
{ title: 'Same surprise audit notice' },
{
  title: '30 seconds to export',
  description: 'One click. Complete, timestamped audit package.'
},
{
  title: 'Single system of record',
  description: 'Immutable version history. Timestamped acknowledgments.'
},
{
  title: 'Pass inspection confidently',
  description: 'Hand the auditor the proof. Everyone goes home on time.'
}];


// ─────────────────────────────────────────────
// SOLUTION CARD MESH GRADIENT
// Richer dark teal mesh — premium, not subtle.
// Deep navy base with animated teal orbs.
// ─────────────────────────────────────────────
const SolutionMesh = () =>
<>
    <style>{`
      @keyframes solutionMesh {
        0%   { background-position: 0% 0%; }
        25%  { background-position: 100% 30%; }
        50%  { background-position: 80% 100%; }
        75%  { background-position: 20% 60%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes solutionOrb1 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(40px,-30px) scale(1.25); }
      }
      @keyframes solutionOrb2 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(-30px,40px) scale(1.2); }
      }
      @keyframes solutionOrb3 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(20px,30px) scale(1.1); }
        66%     { transform: translate(-20px,-15px) scale(0.9); }
      }
    `}</style>

    <div
    className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none"
    style={{ zIndex: 0 }}>
    
      {/* Deep navy/teal base — shifts slowly */}
      <div style={{
      position: 'absolute', inset: 0,
      background: 'linear-gradient(135deg, #00022e 0%, #02075d 35%, #0a3d3d 65%, #002a2a 100%)',
      backgroundSize: '300% 300%',
      animation: 'solutionMesh 16s ease-in-out infinite',
      borderRadius: 'inherit'
    }} />

      {/* Orb 1 — rich teal top-right */}
      <div style={{
      position: 'absolute', top: '-15%', right: '-10%',
      width: '60%', height: '60%', borderRadius: '50%',
      filter: 'blur(55px)',
      animation: 'solutionOrb1 11s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(51,153,153,0.45) 0%, rgba(13,148,136,0.2) 40%, transparent 70%)'
    }} />

      {/* Orb 2 — deep teal bottom-left */}
      <div style={{
      position: 'absolute', bottom: '-10%', left: '-10%',
      width: '55%', height: '55%', borderRadius: '50%',
      filter: 'blur(60px)',
      animation: 'solutionOrb2 13s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(13,148,136,0.35) 0%, rgba(2,7,93,0.2) 50%, transparent 70%)'
    }} />

      {/* Orb 3 — periwinkle center shimmer */}
      <div style={{
      position: 'absolute', top: '30%', left: '20%',
      width: '50%', height: '50%', borderRadius: '50%',
      filter: 'blur(70px)',
      animation: 'solutionOrb3 15s ease-in-out infinite',
      background: 'radial-gradient(circle, rgba(100,130,255,0.15) 0%, transparent 70%)'
    }} />
    </div>
  </>;


// ─────────────────────────────────────────────
// COMPARISON CARD
// ─────────────────────────────────────────────
const ComparisonCard = ({
  type,
  title,
  points,
  footerTitle,
  footerValue,
  footerSub
}: CardProps) => {
  const isProblem = type === 'problem';
  const [isHovered, setIsHovered] = React.useState(false);

  // Problem card — light
  const problemAccent = '#ef4444';
  const problemBadgeBg = '#fee2e2';
  const problemBorder = isHovered ? '#f87171' : '#fca5a5';
  const problemIconColor = isHovered ? '#ef4444' : '#fca5a5';

  // Solution card — dark
  const solutionAccent = '#339999';
  const solutionBorder = isHovered ? 'rgba(51,153,153,0.6)' : 'rgba(51,153,153,0.35)';

  const cardBorder = isProblem ? problemBorder : solutionBorder;

  const cardBoxShadow = isProblem ?
  isHovered ?
  '0 8px 40px rgba(239,68,68,0.10), 0 2px 12px rgba(239,68,68,0.07)' :
  '0 4px 24px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05)' :
  isHovered ?
  '0 16px 60px rgba(51,153,153,0.35), 0 4px 20px rgba(2,7,93,0.4)' :
  '0 8px 40px rgba(51,153,153,0.2), 0 2px 12px rgba(2,7,93,0.3)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      whileHover={{ y: isProblem ? -4 : -8, transition: { duration: 0.3 } }}
      transition={{
        duration: 0.6,
        ease: [0.23, 1, 0.32, 1],
        delay: isProblem ? 0 : 0.1
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative w-full max-w-md flex flex-col h-full mx-auto">
      
      <div
        className="relative rounded-3xl p-8 md:p-10 flex flex-col h-full"
        style={{
          background: isProblem ? '#ffffff' : 'transparent',
          border: `1.5px solid ${cardBorder}`,
          boxShadow: cardBoxShadow,
          transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
          overflow: 'hidden'
        }}>
        
        {/* Animated mesh — solution card only */}
        {!isProblem && <SolutionMesh />}

        <div className="relative z-10 flex flex-col h-full">

          {/* Badge + Heading */}
          <header className="mb-8 text-left">
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
              style={
              isProblem ?
              { background: problemBadgeBg, border: '1px solid #fecaca', color: problemAccent } :
              { background: 'rgba(51,153,153,0.15)', border: '1px solid rgba(51,153,153,0.4)', color: '#5ecfcf' }
              }>
              
              {isProblem ?
              <AlertTriangle className="w-3.5 h-3.5" /> :
              <ShieldCheck className="w-3.5 h-3.5" />}
              {isProblem ? 'Without Veracord' : 'With Veracord'}
            </motion.div>

            <h3
              className="text-3xl font-bold tracking-tight"
              style={{ color: isProblem ? '#0f172a' : '#ffffff' }}>
              
              {title}
            </h3>
          </header>

          {/* Points */}
          <div className="flex-grow space-y-6">
            {points.map((point, idx) =>
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + idx * 0.08 }}
              className="flex gap-4">
              
                <div className="mt-0.5 flex-shrink-0">
                  {isProblem ?
                <XCircle
                  className="w-5 h-5 transition-colors duration-300"
                  style={{ color: problemIconColor }} /> :


                <motion.div
                  animate={{ scale: [1, 1.12, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: idx * 0.3 }}>
                  
                      <CheckCircle2
                    className="w-5 h-5"
                    style={{ color: solutionAccent }} />
                  
                    </motion.div>
                }
                </div>

                <div>
                  <p
                  className="text-base leading-snug font-semibold"
                  style={{ color: isProblem ? '#0f172a' : '#ffffff' }}>
                  
                    {point.title}
                  </p>
                  {point.description &&
                <p
                  className="text-sm leading-relaxed mt-0.5"
                  style={{ color: isProblem ? '#475569' : 'rgba(255,255,255,0.55)' }}>
                  
                      {point.description}
                    </p>
                }
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          {footerValue &&
          <footer
            className="mt-10 pt-8 flex flex-col gap-1 items-center text-center"
            style={{
              borderTop: `1px solid ${isProblem ? '#f1f5f9' : 'rgba(51,153,153,0.25)'}`
            }}>
            
              <span
              className="text-xs font-medium uppercase tracking-widest"
              style={{ color: isProblem ? '#94a3b8' : 'rgba(255,255,255,0.45)' }}>
              
                {footerTitle}
              </span>
              <div
              className="text-2xl font-bold mt-1"
              style={{ color: isProblem ? '#0f172a' : '#ffffff' }}>
              
                {footerValue}
              </div>
              {!isProblem &&
            <p
              className="text-xs mt-1"
              style={{ color: 'rgba(255,255,255,0.45)' }}>
              
                  or $207/mo billed annually
                </p>
            }
              {footerSub &&
            <p
              className="text-sm font-medium"
              style={{ color: isProblem ? '#94a3b8' : solutionAccent }}>
              
                  {footerSub}
                </p>
            }
            </footer>
          }

        </div>
      </div>
    </motion.div>);

};

// ─────────────────────────────────────────────
// CONNECTOR ARROW
// ─────────────────────────────────────────────
const ConnectorArrow = () =>
<motion.div
  className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 p-3 rounded-full items-center justify-center"
  animate={{
    boxShadow: [
    '0 0 0 0px rgba(51,153,153,0.3)',
    '0 0 0 8px rgba(51,153,153,0.08)',
    '0 0 0 0px rgba(51,153,153,0.3)']

  }}
  transition={{ duration: 2.5, repeat: Infinity }}
  style={{
    background: '#ffffff',
    border: '1.5px solid #99f6e4',
    boxShadow: '0 2px 16px rgba(51,153,153,0.15)'
  }}>
  
    <ArrowRight className="w-[30px] h-[30px]" style={{ color: '#339999' }} />
  </motion.div>;


// ─────────────────────────────────────────────
// COMPARISON SECTION
// ─────────────────────────────────────────────
export const ComparisonSection = () =>
<section
  className="w-full py-24 px-6 md:px-12 flex flex-col items-center justify-center relative overflow-hidden"
  style={{ background: '#f2f2f2', fontFamily: 'Inter, sans-serif' }}>
  
    <div className="max-w-6xl w-full relative z-10">

      {/* Section Header */}
      <div className="text-center mb-20">
        <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}>
        
          <h2
          className="text-4xl md:text-6xl tracking-tight mb-6"
          style={{ color: '#0f172a', fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
          You Know This Story

        </h2>

          {/* Teal underline accent */}
          <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
          className="h-[3px] w-12 mx-auto rounded-full mb-8 origin-left"
          style={{ background: 'linear-gradient(90deg, #339999, #06b6d4)' }} />
        

          <p
          className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
          style={{ color: '#475569' }}>
          
            Every clinic knows the audit scramble. Veracord makes it impossible.
          </p>
        </motion.div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-stretch relative">
        <ConnectorArrow />
        <ComparisonCard
        type="problem"
        title="The Two Weeks of Hell"
        points={PROBLEM_POINTS}
        footerTitle="Cost to your clinic"
        footerValue="$5,000–$10,000"
        footerSub="In lost productivity. Every audit." />
      
        <ComparisonCard
        type="solution"
        title="Audit-Ready in Seconds"
        points={SOLUTION_POINTS}
        footerTitle="Veracord subscription"
        footerValue="$249 / month"
        footerSub="Pays for itself the first audit." />
      
      </div>

      {/* Closing line */}
      <motion.p
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.6, duration: 0.6 }}
      className="text-center text-sm mt-16"
      style={{ color: '#94a3b8' }}>No setup fees. No contracts. Audit-ready in minutes.


    </motion.p>

    </div>
  </section>;


export default ComparisonSection;